import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type MouseEvent } from 'react';

import type { ParticipantId, WallParticipant, WallGroup } from '@walls/store';

interface ParticipantsListProps {
  canManageParticipants: boolean;
  currentParticipantId: ParticipantId | null;
  isFilteringCards: boolean;
  participants: WallParticipant[];
  groups: WallGroup[];
  visibleParticipantIds: ParticipantId[];
  onDeleteParticipant: (participantId: ParticipantId) => void;
  onToggleVisibleParticipant: (participantId: ParticipantId) => void;
}

export function ParticipantsList({
  canManageParticipants,
  currentParticipantId,
  isFilteringCards,
  participants,
  groups,
  visibleParticipantIds,
  onDeleteParticipant,
  onToggleVisibleParticipant,
}: ParticipantsListProps) {
  const [participantMenuAnchor, setParticipantMenuAnchor] = useState<HTMLElement | null>(null);
  const [participantMenuId, setParticipantMenuId] = useState<ParticipantId | null>(null);
  const participantGroupsById = useMemo(() => {
    const groupNamesByParticipantId = new Map<ParticipantId, string[]>();

    groups.forEach((group) => {
      group.participantIds.forEach((participantId) => {
        const groupNames = groupNamesByParticipantId.get(participantId) ?? [];
        groupNames.push(group.name);
        groupNamesByParticipantId.set(participantId, groupNames);
      });
    });

    return groupNamesByParticipantId;
  }, [groups]);

  const openParticipantMenu = (
    event: MouseEvent<HTMLButtonElement>,
    participantId: ParticipantId
  ) => {
    setParticipantMenuAnchor(event.currentTarget);
    setParticipantMenuId(participantId);
  };

  const closeParticipantMenu = () => {
    setParticipantMenuAnchor(null);
    setParticipantMenuId(null);
  };

  return (
    <List dense disablePadding sx={{ py: 0.75 }}>
      {participants.map((participant) => {
        const isSelected = visibleParticipantIds.includes(participant.id);
        const isDimmed = isFilteringCards && !isSelected;
        const canDeleteParticipant =
          canManageParticipants && participant.id !== currentParticipantId;
        const participantGroupNames = participantGroupsById.get(participant.id) ?? [];

        return (
          <ListItem
            key={participant.id}
            disablePadding
            sx={{
              display: 'block',
              px: 1,
              py: 0.25,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Box
                component="button"
                type="button"
                aria-pressed={isSelected}
                onClick={() => onToggleVisibleParticipant(participant.id)}
                sx={{
                  alignItems: 'center',
                  bgcolor: isSelected ? '#e7f7fb' : 'transparent',
                  border: '1px solid',
                  borderColor: isSelected ? '#6bb7ca' : 'transparent',
                  borderRadius: '8px',
                  color: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  flex: '1 1 auto',
                  gap: 1,
                  minWidth: 0,
                  opacity: isDimmed ? 0.52 : 1,
                  px: 1,
                  py: 0.75,
                  textAlign: 'left',
                  transition:
                    'background-color 120ms ease, border-color 120ms ease, opacity 120ms ease',
                  '&:hover': {
                    bgcolor: isSelected ? '#dff2f7' : '#f3f4f6',
                  },
                }}
              >
                <Box sx={{ flex: '0 0 auto', position: 'relative', gap: 0.5 }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      bgcolor: participant.isLive ? '#12b76a' : '#98a2b3',
                      border: '2px solid #fff',
                      borderRadius: '50%',
                      height: 16,
                      width: 16,
                    }}
                  />
                </Box>
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                      <Typography component="span" variant="body2" noWrap sx={{ minWidth: 0 }}>
                        {participant.name}
                      </Typography>
                      {participant.id === currentParticipantId && (
                        <Box
                          component="span"
                          sx={{
                            bgcolor: '#eef2ff',
                            borderRadius: '999px',
                            color: '#3538cd',
                            flex: '0 0 auto',
                            fontSize: 11,
                            lineHeight: '16px',
                            px: 0.75,
                          }}
                        >
                          You
                        </Box>
                      )}
                    </Stack>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondary={
                    participantGroupNames.length > 0 ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ mt: 0.25 }}
                      >
                        {participantGroupNames.map((groupName) => (
                          <Chip
                            key={groupName}
                            label={groupName}
                            size="small"
                            sx={{
                              height: 18,
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                fontSize: 11,
                                px: 0.75,
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    ) : null
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                  sx={{ minWidth: 0, my: 0 }}
                />
              </Box>
              <Tooltip title={isSelected ? 'Hide from focus' : 'Show in focus'}>
                <IconButton
                  size="small"
                  aria-label={isSelected ? `Hide ${participant.name}` : `Show ${participant.name}`}
                  onClick={() => onToggleVisibleParticipant(participant.id)}
                  sx={{
                    bgcolor: isSelected ? '#0f2a39' : 'transparent',
                    color: isSelected ? '#fff' : 'text.secondary',
                    flex: '0 0 auto',
                    '&:hover': {
                      bgcolor: isSelected ? '#16384b' : '#f3f4f6',
                    },
                  }}
                >
                  {isSelected ? (
                    <VisibilityIcon fontSize="small" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              {canDeleteParticipant && (
                <Tooltip title="Participant actions">
                  <IconButton
                    size="small"
                    aria-label={`${participant.name} actions`}
                    aria-controls={
                      participantMenuId === participant.id ? 'participant-actions-menu' : undefined
                    }
                    aria-expanded={participantMenuId === participant.id ? true : undefined}
                    aria-haspopup="menu"
                    onClick={(event) => openParticipantMenu(event, participant.id)}
                    sx={{ color: 'text.secondary', flex: '0 0 auto' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {canDeleteParticipant && (
              <Menu
                id="participant-actions-menu"
                anchorEl={participantMenuAnchor}
                open={participantMenuId === participant.id}
                onClose={closeParticipantMenu}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              >
                <MenuItem
                  onClick={() => {
                    closeParticipantMenu();
                    onDeleteParticipant(participant.id);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  Remove participant
                </MenuItem>
              </Menu>
            )}
          </ListItem>
        );
      })}
    </List>
  );
}
